import cities from '../data/cities.json';

export type ServiceType = 
  | 'valuta-la-tua-auto'
  | 'vendi-la-tua-auto'
  | 'acquisto-auto-istantaneo'
  | 'ispezione-pre-acquisto'
  | 'valutazione-professionale-dell-auto'
  | 'vendi-la-tua-auto-ora';

interface ServiceConfig {
  path: string;
  title: string;
  description: string;
}

export const services: Record<ServiceType, ServiceConfig> = {
  'valuta-la-tua-auto': {
    path: 'valuta-la-tua-auto', 
    title: 'Valuta la tua auto',
    description: 'Ottieni una valutazione professionale istantanea per la tua auto'
  },
  'vendi-la-tua-auto': {
    path: 'vendi-la-tua-auto',
    title: 'Vendi la tua auto',
    description: 'Ottieni il miglior prezzo per la tua auto con il nostro servizio di acquisto auto professionale'
  },
  'acquisto-auto-istantaneo': {
    path: 'acquisto-auto-istantaneo',
    title: 'Acquisto auto istantaneo',
    description: 'Acquistiamo la tua auto istantaneamente con pagamento lo stesso giorno e ritiro gratuito'
  },
  'ispezione-pre-acquisto': {
    path: 'ispezione-pre-acquisto',
    title: 'Ispezione pre-acquisto',
    description: 'Ispezione professionale del veicolo prima di acquistare la tua auto'
  },
  'valutazione-professionale-dell-auto': {
    path: 'valutazione-professionale-dell-auto',
    title: 'Valutazione professionale dell\'auto',
    description: 'Ottieni un valore di mercato accurato per il tuo veicolo dai nostri esperti'
  },
  'vendi-la-tua-auto-ora': {
    path: 'vendi-la-tua-auto-ora',
    title: 'Vendi la tua auto ora',
    description: 'Processo di vendita dell\'auto rapido e senza problemi con pagamento istantaneo'
  }
};

// Centralizzata funzione per contenuti dei servizi
export const getServiceContent = (service: ServiceType, city?: string) => {
  const capitalizedCity = city ? capitalizeCityName(city) : '';
  const cityText = capitalizedCity ? ` a ${capitalizedCity}` : '';
  const cityUpperText = capitalizedCity ? ` A ${capitalizedCity.toUpperCase()}` : '';

  switch (service) {
    case 'valuta-la-tua-auto':
      return {
        title: capitalizedCity ? `Valutazione Auto${cityText}` : "Valutazione Auto Rapida e Senza Stress",
        subtitle: capitalizedCity ? `SCOPRI IL VALORE DELLA TUA AUTO${cityUpperText}` : "VENDI LA TUA AUTO SENZA STRESS",
        description: capitalizedCity 
          ? `Ottieni una valutazione professionale per la tua auto${cityText} in pochi minuti. Servizio rapido, affidabile e senza stress per conoscere il reale valore di mercato del tuo veicolo.`
          : "Ottieni il miglior prezzo per la tua auto in 3 semplici passaggi: Valuta, Ispeziona e Vendi. Nessuna commissione nascosta, nessuna seccatura di documenti, pagamento immediato!",
        seoTitle: capitalizedCity ? `Valutazione Auto ${capitalizedCity} | Preventivo Gratuito | ValutaAuto` : "Valutazione Auto Rapida | ValutaAuto",
        seoDescription: capitalizedCity 
          ? `Valutazione professionale auto ${capitalizedCity}. Preventivo gratuito, servizio rapido e pagamento immediato. Scopri quanto vale la tua auto oggi stesso!`
          : "Valutazione auto professionale e gratuita. Ottieni il miglior prezzo per la tua auto con servizio rapido e pagamento immediato."
      };
    
    case 'vendi-la-tua-auto':
      return {
        title: capitalizedCity ? `Vendi la Tua Auto${cityText}` : "Vendi la Tua Auto Rapidamente",
        subtitle: capitalizedCity ? `VENDI LA TUA AUTO${cityUpperText} SENZA PROBLEMI` : "VENDI LA TUA AUTO SENZA PROBLEMI",
        description: capitalizedCity 
          ? `Vendi la tua auto${cityText} senza stress. Gestiamo tutto noi: pratiche, ispezione e pagamento immediato. Servizio professionale con il miglior prezzo di mercato.`
          : "Vendi la tua auto senza stress. Gestiamo tutto noi: pratiche, ispezione e pagamento immediato.",
        seoTitle: capitalizedCity ? `Vendi Auto ${capitalizedCity} | Miglior Prezzo Garantito | ValutaAuto` : "Vendi la Tua Auto | Miglior Prezzo | ValutaAuto",
        seoDescription: capitalizedCity 
          ? `Vendi la tua auto ${capitalizedCity} al miglior prezzo. Pagamento immediato, ritiro gratuito e gestione completa delle pratiche. Servizio professionale garantito.`
          : "Vendi la tua auto al miglior prezzo di mercato. Pagamento immediato, ritiro gratuito e gestione completa delle pratiche."
      };

    case 'acquisto-auto-istantaneo':
      return {
        title: capitalizedCity ? `Acquisto Auto Immediato${cityText}` : "Acquisto Auto Immediato",
        subtitle: capitalizedCity ? `ACQUISTO AUTO IMMEDIATO${cityUpperText}` : "ACQUISTO AUTO IMMEDIATO",
        description: capitalizedCity 
          ? `Ricevi un'offerta immediata per la tua auto${cityText}. Nessuna attesa, nessuna contrattazione - solo un prezzo equo, pagamento in giornata e ritiro gratuito del veicolo.`
          : "Ricevi un'offerta immediata per la tua auto. Nessuna attesa, nessuna contrattazione - solo un prezzo equo e pagamento in giornata.",
        seoTitle: capitalizedCity ? `Acquisto Auto Immediato ${capitalizedCity} | Pagamento in Giornata | ValutaAuto` : "Acquisto Auto Immediato | Pagamento in Giornata | ValutaAuto",
        seoDescription: capitalizedCity 
          ? `Acquisto auto immediato ${capitalizedCity}. Offerta istantanea, pagamento in giornata e ritiro gratuito. Vendi la tua auto senza attese e al miglior prezzo.`
          : "Acquisto auto immediato con pagamento in giornata. Offerta istantanea, ritiro gratuito e prezzi di mercato competitivi."
      };

    case 'ispezione-pre-acquisto':
      return {
        title: capitalizedCity ? `Ispezione Auto${cityText}` : "Ispezione Auto Professionale",
        subtitle: capitalizedCity ? `ISPEZIONE AUTO PROFESSIONALE${cityUpperText}` : "ISPEZIONE AUTO PROFESSIONALE",
        description: capitalizedCity 
          ? `Servizio di ispezione professionale del veicolo${cityText}. Valutazione accurata da esperti certificati, rapporto dettagliato e tranquillità.`
          : "Servizio di ispezione professionale del veicolo. Valutazione accurata da esperti certificati e rapporto dettagliato.",
        seoTitle: capitalizedCity ? `Ispezione Auto ${capitalizedCity} | Valutazione Professionale | ValutaAuto` : "Ispezione Auto Professionale | ValutaAuto",
        seoDescription: capitalizedCity 
          ? `Ispezione auto professionale ${capitalizedCity}. Valutazione accurata da esperti, rapporto dettagliato e consulenza specializzata per la tua tranquillità.`
          : "Ispezione auto professionale con valutazione accurata, rapporto dettagliato e consulenza specializzata."
      };

    case 'valutazione-professionale-dell-auto':
      return {
        title: capitalizedCity ? `Valutazione Auto Professionale${cityText}` : "Valutazione Auto Professionale",
        subtitle: capitalizedCity ? `VALUTAZIONE AUTO ESPERTA${cityUpperText}` : "VALUTAZIONE AUTO ESPERTA",
        description: capitalizedCity 
          ? `Ottieni un valore di mercato accurato per il tuo veicolo${cityText}. I nostri esperti automobilistici offrono valutazioni dettagliate e affidabili.`
          : "Ottieni un valore di mercato accurato per il tuo veicolo dai nostri esperti automobilistici.",
        seoTitle: capitalizedCity ? `Valutazione Auto Professionale ${capitalizedCity} | Esperti Certificati | ValutaAuto` : "Valutazione Auto Professionale | Esperti Certificati | ValutaAuto",
        seoDescription: capitalizedCity 
          ? `Valutazione auto professionale ${capitalizedCity} da esperti certificati. Analisi di mercato accurata, report dettagliato e consulenza specializzata.`
          : "Valutazione auto professionale da esperti certificati con analisi di mercato accurata e report dettagliato."
      };

    case 'vendi-la-tua-auto-ora':
      return {
        title: capitalizedCity ? `Vendi la Tua Auto Ora${cityText}` : "Vendi la Tua Auto Ora",
        subtitle: capitalizedCity ? `VENDITA RAPIDA DI AUTO${cityUpperText}` : "VENDITA RAPIDA DI AUTO",
        description: capitalizedCity 
          ? `Pronto a vendere la tua auto rapidamente${cityText}? Ottieni preventivi immediati e pagamento in giornata. Prova un processo di vendita auto senza problemi con il nostro team di professionisti.`
          : "Pronto a vendere la tua auto rapidamente? Ottieni preventivi immediati e pagamento in giornata.",
        seoTitle: capitalizedCity ? `Vendi Auto Ora ${capitalizedCity} | Processo Rapido | ValutaAuto` : "Vendi Auto Ora | Processo Rapido | ValutaAuto",
        seoDescription: capitalizedCity 
          ? `Vendi auto ora ${capitalizedCity} con processo rapido e pagamento immediato. Team professionale, miglior prezzo di mercato e gestione completa.`
          : "Vendi auto ora con processo rapido, pagamento immediato e team professionale. Miglior prezzo di mercato garantito."
      };

    default:
      return {
        title: capitalizedCity ? `Servizio Auto${cityText}` : 'Servizio Auto',
        subtitle: capitalizedCity ? `SERVIZIO AUTO${cityUpperText}` : 'SERVIZIO AUTO',
        description: capitalizedCity ? `Servizio professionale per la tua auto${cityText}.` : 'Servizio professionale per tutte le tue esigenze auto.',
        seoTitle: capitalizedCity ? `Servizio Auto ${capitalizedCity} | ValutaAuto` : `Servizio Auto | ValutaAuto`,
        seoDescription: capitalizedCity ? `Servizio auto professionale ${capitalizedCity}. Servizio professionale e affidabile.` : `Servizio auto professionale. Servizio professionale e affidabile.`
      };
  }
};

// Helper function to create URL-friendly slug from city name
export const createCitySlug = (cityName: string): string => {
  return cityName
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

// Helper function to properly capitalize city names
export const capitalizeCityName = (cityName: string): string => {
  if (!cityName) return '';
  
  // Special cases for Italian cities
  const specialCases: Record<string, string> = {
    'reggio emilia': 'Reggio Emilia',
    'reggio nell\'emilia': 'Reggio nell\'Emilia',
    'sesto san giovanni': 'Sesto San Giovanni',
    'cinisello balsamo': 'Cinisello Balsamo',
    'busto arsizio': 'Busto Arsizio',
    'valle d\'aosta': 'Valle d\'Aosta',
    'trentino-alto adige': 'Trentino-Alto Adige',
    'friuli-venezia giulia': 'Friuli-Venezia Giulia'
  };
  
  const lowerCity = cityName.toLowerCase().trim();
  
  // Check if it's a special case
  if (specialCases[lowerCity]) {
    return specialCases[lowerCity];
  }
  
  // General capitalization rules for Italian cities
  return cityName
    .toLowerCase()
    .split(' ')
    .map(word => {
      // Handle words with apostrophes (like "dell'", "d'", etc.)
      if (word.includes("'")) {
        return word.split("'").map(part => 
          part.charAt(0).toUpperCase() + part.slice(1)
        ).join("'");
      }
      // Handle words with hyphens
      if (word.includes('-')) {
        return word.split('-').map(part => 
          part.charAt(0).toUpperCase() + part.slice(1)
        ).join('-');
      }
      // Regular word capitalization
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

export const generateSeoRoutes = () => {
  const routes: { 
    path: string; 
    pattern: string; 
    city: string; 
    citySlug: string; 
    region: string; 
    service: ServiceType;
    fullTitle: string;
    fullDescription: string;
  }[] = [];
  
  Object.entries(services).forEach(([serviceKey, serviceConfig]) => {
    cities.forEach(city => {
      const citySlug = createCitySlug(city);
      const content = getServiceContent(serviceKey as ServiceType, city);
      
      const route = {
        path: `/${serviceConfig.path}-${citySlug}`,
        pattern: `/${serviceConfig.path}-:city`,
        city: city,
        citySlug: citySlug,
        region: city,
        service: serviceKey as ServiceType,
        fullTitle: content.seoTitle,
        fullDescription: content.seoDescription
      };
      
      routes.push(route);
    });
  });

  console.log(`📊 SEO Routes: Generated ${routes.length} routes for ${Object.keys(services).length} services across ${cities.length} cities`);
  
  return routes;
};

export const getAllCitySlugs = (): string[] => {
  return cities.map(city => createCitySlug(city));
};

export const getCityFromSlug = (slug: string): string | undefined => {
  return cities.find(city => createCitySlug(city) === slug);
};

// Funzione per ottenere contenuti localizzati per FAQ e altri componenti
export const getLocalizedContent = (city?: string, service?: ServiceType) => {
  const capitalizedCity = city ? capitalizeCityName(city) : '';
  const cityText = capitalizedCity ? ` a ${capitalizedCity}` : '';
  
  return {
    faq: {
      title: capitalizedCity ? `Domande Frequenti${cityText}` : 'Domande Frequenti',
      description: capitalizedCity 
        ? `Trova risposte alle domande più comuni sul nostro servizio di acquisto auto${cityText}`
        : 'Trova risposte alle domande più comuni sul nostro servizio di acquisto auto'
    },
    service: service ? getServiceContent(service, city) : getServiceContent('valuta-la-tua-auto', city)
  };
};

export const generateSitemap = () => {
  const baseUrl = 'https://valutaauto24.it';
  const routes = generateSeoRoutes();
  
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>${baseUrl}</loc>
    <changefreq>daily</changefreq>
    <priority>1.0</priority>
  </url>
  ${routes.map(route => `
  <url>
    <loc>${baseUrl}${route.path}</loc>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>
  `).join('')}
</urlset>`;

  return xml;
}; 